import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as lodash from 'lodash-es';
import { CommonService } from 'src/app/shared/services/common.service';
import * as moment from 'moment';
import { CustomHttpParams } from 'src/app/shared/loader/custom-httpParam';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { BehaviorSubject } from 'rxjs';
import objectMappers from '../../../../../../iotah-common/mappers/mapper.json';
import { mainDeviceConfig } from './device-factory/device-config';
import { DeviceFactory } from './device-factory/device-factory';
import { ValidationService } from 'src/app/shared/services/validation.service';
import { DeviceCommonService } from './device-common.service';
import { DeviceTypeService } from './device-type.service';
import * as imDeviceWarningsDecoder from '../../../../../../Common/devices-config/imDeviceWarningsDecoder';

export interface DeviceCommand {
	id: number;
	mac_address: string;
	user_id: string;
	commands: {
		cmds: string[];
	};
	changes: {
		[key: string]: any;
	};
	time: string;
	wait_time: number;
	command_info: Record<string, any>;
	serial_number: string;
	siteId: number;
	customerId: number;
	user_name: string;
	customer_name: string;
	site_name: string;
	expiry_date: number;
	last_connect_time: number
}

export interface ImPowerThresholdValues {
	[field: string]: string | number | null | undefined;
}

@Injectable({
	providedIn: 'root'
})
export class DeviceService {
	private updateConfigDataSbj = new BehaviorSubject<{isDone: boolean, data: Object}>({isDone: false, data: {}});
	updateConfigData = this.updateConfigDataSbj.asObservable();
	route = '/device/';

	// event related params
	notDisplayedEventFields = [
		"event_flags",
		"global_event_flags",
		"isValidCRC16",
		"is_ok_record",
		"previous_non_same_event_type",
		"end_timestamp_utc",
		"max_temperature_recorded_timestamp_utc",
		"timestamp_utc",
		"safe_close",
		'soc_set',
		'calc_duration',
		'restart_reason_l2',
	];

	eventCalculatedFieldsMapper = {
		inuse_as: "inuse_ahr",
		inuse_ws: "inuse_kwhr",
		charge_as: "charge_ahr",
		charge_ws: "charge_kwhr"
	}

	readonly lastConnectionType = [
		{ label: this.translateService.instant('client_socket_type.0'), value: 0 },
		{ label: this.translateService.instant('client_socket_type.2'), value: 2 },
	];

	readonly isSmartRebate = [
		{ label: this.translateService.instant('g.no'), value: 0 },
		{ label: this.translateService.instant('g.yes'), value: 1 },
	];

	// ST-2649, ST-2779: values match deviceDAO.CONNECTIVITY_DEVICE_TYPES on the backend
	readonly connectivityDeviceTypes = [
		{ label: this.translateService.instant('g.iotah'), value: 'truklink' },
		{ label: this.translateService.instant('g.iotah_ps'), value: 'truklink_ps' },
		{ label: this.translateService.instant('g.charglink'), value: 'charglink' },
		{ label: this.translateService.instant('g.battlink'), value: 'battlink' },
		{ label: this.translateService.instant('g.battlink_ii'), value: 'battlink_ii' },
		{ label: this.translateService.instant('g.lithium_battlink'), value: 'lithium_battlink' },
		{ label: this.translateService.instant('g.brm'), value: 'brm' },
		{ label: this.translateService.instant('g.eMeter'), value: 'iotemeter_sp1' },
		{ label: this.translateService.instant('g.iotemeter'), value: 'iotemeter_sp3' },
		{ label: this.translateService.instant('g.three_phase_external'), value: 'iotemeter_pro' },
		{ label: this.translateService.instant('g.acu_rev'), value: 'iotemeter_edge' },
	];

	eventsFieldsBasedOnFW = [
		'hot_disconnection',
		'zone_change',
	];

	eventsDefaultFields = [
		"sequence_id",
		"timestamp",
		"event_type",
		"end_timestamp",
		"event_duration",
		"inuse_ahr",
		"inuse_kwhr",
		"charge_ahr",
		"charge_kwhr",
		"billed_kwh",
		"accurate_inuse_seconds",
		"start_voltage",
		"min_voltage",
		"max_voltage",
		"end_voltage",
		"event_max_current",
		"event_min_current",
		"event_average_current",
		"insertion_time",
		"accurate_charge_seconds",
		'latitude',
		'longitude',
		"system_start",
		"fw_update",
		"set_rtc",
		"lift_time",
		"travel_time",
		"soc_set",
		"start_soc",
		"end_soc",
		"finish_reason",
		"eq_reason",
		'zone_change',
		"restart_reason",
	];

	imEventsDefaultFields = [
		'sequence_id',
		'timestamp',
		'end_timestamp',
		'event_type',
		'calc_duration',
		'event_consumed_energy',
		'event_generated_energy',
		'negative_peak_power',
		'positive_peak_power',
		'consumed_energy_duration',
		'generated_energy_duration',
		'min_voltage',
		'max_voltage',
		'max_current',
		'modbus_failed',
		'status_flag_safe_close',
		'system_start',
		'set_rtc',
		'my_zone_id',
		'ct_set',
		'insertion_time',
		'update_config',
		// 'wiring_mode_set',
		'meter_changed',
		'event_generated_kvarh',
		'event_consumed_kvarh',
		'hist_generated_kwh',
		'hist_consumed_kwh',
		'latitude',
		'longitude',
		'full_power_reset',
		'midnight_split',
		'time_change_split',
		'first_time_configuration',
		'fw_update',
		'set_serial_number',
		'user_restart_request',
	];

	eventsNocAccessFields = [
		'billed_kwh',
		'event_min_current'
	];
	eventsAdminFields = [
		'latitude',
		'longitude',
	];
	eventsDebugDataFields = [
		'restart_reason',
	];
	eventTypes = {
		1: 'charge',
		2: 'idle',
		3: 'inuse',
	};

	eqReasons = {
		1: 'timeout',
		2: 'target_voltage_reached',
		3: 'target_voltage_reached_timeout'
	};

	finishReasons = {
		0: 'dvdt',
		1: 'timeout',
		2: 'target_voltage_reached',
		3: 'target_voltage_reached_timeout'
	};

	restartReasons = {
		0: {
			0x00000000: 'l2_0_0',
			0x00000010: 'l2_0_1',
			0x00000020: 'l2_0_2',
			0x00000030: 'l2_0_3',
			0x00000040: 'l2_0_4',
			0x00000050: 'l2_0_5',
			0x00000060: 'l2_0_6',
			0x00000070: 'l2_0_7',
			0x00000080: 'l2_0_8',
			0x00000090: 'l2_0_9',
			0x000000A0: 'l2_0_A0',
			0x000000B0: 'l2_0_B0',
			0x000000C0: 'l2_0_C0',
			0x000000D0: 'l2_0_D0',
			0x000000E0: 'l2_0_E0',
			0x000000F0: 'l2_0_F0',
		},
		1: {
			0x00000010: 'l2_1_1',
			0x00000020: 'l2_1_2',
			0x00000030: 'l2_1_3',
			0x00000040: 'l2_1_4',
			0x00000050: 'l2_1_5',
			0x00000060: 'l2_1_6',
		}
	};

	batteryTypes = {
		1: 'lead',
		3: 'lithuim_ion',
		4: 'n/a',
	};

	chargerTypes = {
		Conventional: 0,
		Fast: 1,
		Opportunity: 2,
	};

	eventTypesByName = {
		charge: 1,
		idle: 2,
		inuse: 3
	};
	batteryTypesByName = {
		lead: 0,
		lithuim_ion: 2,
		other: 3
	};

	simManagementTabs = {
		usage: 'usage',
		disabled: 'disabled',
		blocked: 'blocked',
		missingFromOnce: 'missingFromOnce'
	};

	deviceBooleanFields = [
		'in_test_mode', 'setup_done', 'setup_reset_data', 'replacement_part', 'enable_rt', 'has_water_level',
		'has_temperature_sensor', 'has_current_low_range', 'enable_multicast', 'enable_wifi_sta',
		'enable_wifi_ap_on_start', 'enable_serversocket', 'is_online_enabled', 'enable_cellular',
		'disable_xtra', 'disable_external_rtc', 'inherit_shift_schedule_site_settings'
	];
	deviceDateFields = [
		'installation_date',
		'setup_time',
		'last_pm_date',
		'mis_voltage_time',
		'mis_capacity_time',
	];
	deviceTimeReferenceFields = [
		'last_update_location_time',
	];
	deviceZoneFields = [
		'zone_id',
	];

	objectFields = [
		'mobile_master',
		'mobile_network',
		'router_network',
		'online_network',
		'mp_cal_high_range',
		'mp_cal_low_range'
	];

	deviceFieldsLists = {
		battery_type: {
			'0': this.translateService.instant('events.battery_type_0'),
			'2': this.translateService.instant('events.battery_type_2'),
		},
		priority: {
			'0': this.translateService.instant('priority_options.0'),
			'1': this.translateService.instant('priority_options.1'),
			'2': this.translateService.instant('priority_options.2'),
			'3': this.translateService.instant('priority_options.3'),
			'10': this.translateService.instant('priority_options.10'),
		},
		charger_type: {
			0: this.translateService.instant('devices.conventional_charge'),
			1: this.translateService.instant('devices.fast_charger'),
			2: this.translateService.instant('devices.opportunity_charge'),
		},
		application_type: {
			1: this.translateService.instant('devices.power_studies'),
			2: this.translateService.instant('devices.golf_cart'),
			3: this.translateService.instant('devices.boat'),
		},
		hall_effect_sensor_type: {
			0: this.translateService.instant('devices.dual_screw'),
			1: this.translateService.instant('devices.single_latch'),
			2: this.translateService.instant('devices.single_latch_250'),
			3: this.translateService.instant('devices.dual_latch_750'),
		},
		shift_schedule: {
			8:  this.translateService.instant('site_utilization_settings.single_shift'),
			16: this.translateService.instant('site_utilization_settings.two_shifts'),
			24: this.translateService.instant('site_utilization_settings.three_shifts'),
		}
	};

	hallEffectSensorTypes = {
		dualScrew: 0,
		singleLatch: 1,
		singleLatch250: 2,
		dualLatch750: 3
	};

	wifiAuthMode = {
		"open": "Open",
		"psk": "PSK",
		"wep": "WEP",
		"wpa2_enterprise": "WPA2 ENTERPRISE",
		"wpa2_psk": "WPA2 PSK",
		"wpa_wpa2_psk": "WPA/WPA2 PSK"
	};

	multiSettingsFields = {
		deviceInfo: [
			'installation_date_fmt', 'truck_manufacturer', 'truck_manufacturer_option', 'truck_manufacturing_year', 'truck_type', 'last_pm_date_fmt'
		],
		deviceSettings: [
			'current_idle_to_charge', 'current_idle_to_inuse', 'current_charge_to_idle', 'current_charge_to_inuse', 'current_inuse_to_charge',
			'current_inuse_to_idle', 'remote_flash_timer', 'dead_man_current_limit', 'charge_to_idle_timer', 'charge_to_inuse_timer',
			'inuse_to_charge_timer', 'inuse_to_idle_timer', 'idle_to_charge_timer', 'idle_to_inuse_timer', 'zone_id', 'enable_rt', 'rt_log_frequency', 'rtLogFrequency', 'rtLogFrequencyUnit',
			'has_water_level', 'hall_effect_sensor_type', 'has_temperature_sensor', 'has_current_low_range', 'disable_external_rtc', 'disable_xtra'
		],
		batterySettings: [
			'vpc', 'battery_capacity', 'battery_type', 'number_of_cells'
		],
		cal: [
			'current_drift_adc_low_range', 'voltage_drift_adc', 'voltage_scale_adc', 'current_drift_adc', 'current_scale_adc', 'current_scale_adc_low_range'
		],
		networkingSettings: [
			'soft_ap_exit', 'soft_ap_exit_no_setup', 'cellular_region', 'cell_access_point_name', 'lte_cellular_bands', 'nbiot_cellular_bands', 'cellular_reconnect_time', 'metered_flag'
		],
		networking: [
			'online_network.ssid', 'online_network.bssid', 'online_network.password', 'online_network.priority', 'online_network.type'
		],
		billedKWHrsSettings: [
			'charger_efficiency', 'battery_efficiency', 'return_factor'
		],
		maintenanceSchedule: [
			'report_ahr_opt', 'report_kwhr_opt', 'report_usage_hours_opt', 'report_period_opt',
			'report_ahr', 'report_kwhr', 'report_usage_hours', 'report_period',
		],
		shiftSchedule: [
			'shift_schedule', 'inherit_shift_schedule_site_settings'
		],
	};

	deviceSettingsUiStructure = {
		'info': [
			'setup_key',
			'memory_signature',
			'serial_number',
			"fw_version",
			"installation_date",
			"study_start_event_id",
			"device_id",
			"application_type",
			"truck_id",
			"truck_sn",
			"truck_type",
			"study_name",
			"hw_version",
			"in_test_mode",
			"setup_requested_site",
			"setup_requested_customer",
			"setup_requested_address",
			"setup_replace_mac",
			"setup_user_id",
			"setup_time",
			"setup_done",
			"setup_reset_data",
			"truck_manufacturer",
			"last_pm_date",
			"last_pm_user_id",
			"latitude",
			"longitude",
			"last_latitude",
			"last_longitude",
			"last_update_location_time"
		],
		'deviceSettings': [
			'current_cable_scale',
			'current_idle_to_charge',
			'current_idle_to_inuse',
			'current_charge_to_idle',
			'current_charge_to_inuse',
			'current_inuse_to_charge',
			'current_inuse_to_idle',
			'remote_flash_timer',
			'dead_man_current_limit',
			'charge_to_idle_timer',
			'charge_to_inuse_timer',
			'inuse_to_charge_timer',
			'inuse_to_idle_timer',
			'idle_to_charge_timer',
			'idle_to_inuse_timer',
			'time_hysteresis',
			'operation_mode',
			'replacement_part',
			'zone_id',
			'enable_rt',
			'rt_log_frequency',
			'has_water_level',
			'hall_effect_sensor_type',
			'has_temperature_sensor',
			'has_current_low_range',
			'sct_device_type',
			'disable_external_rtc',
			'disable_xtra'
		],
		'batterySettings': [
			'fi_duration',
			'eq_duration',
			'vpc',
			'trickle_voltage',
			'cv_target_voltage',
			'fi_target_voltage',
			'eq_voltage',
			'trickle_current_rate',
			'cc_rate',
			'cv_end_current_rate',
			'cv_current_step',
			'fi_current_rate',
			'eq_current_rate',
			'battery_capacity',
			'battery_type',
			'number_of_cells',
			'battery_temperature_compensation_in_mv',
			'fi_dt',
			'fi_dv',
			'charger_type'
		],
		'cal': [
			'current_drift_adc_low_range',
			'voltage_drift_adc',
			'voltage_scale_adc',
			'current_drift_adc',
			'current_scale_adc',
			'temperature_beta',
			'current_scale_adc_low_range',
			'temperature_res_dividor',
			'temperature_res_drift',
			'temperature_res_dividor_drift',
			'probe_drift_adc',
			'probe_scale_adc',
			'gallons_count_scale',
			'high_probe_voltage',
		],
		'networkingSettings': [
			'remoter_server_port',
			'soft_ap_exit_no_setup',
			'mobile_port',
			'master_port',
			'reconnect_time',
			'server_socket_port',
			'remoter_server_ip',
			'ap_password',
			'ap_ssid',
			'ap_channel',
			'soft_ap_exit',
			'enable_wifi_ap_on_start',
			'enable_serversocket',
			'enable_multicast',
			'multicast_port',
			'multicast_ip',
			'is_online_enabled',
			'enable_cellular',
			'cellular_region',
			'cell_access_point_name',
			'lte_cellular_bands',
			'nbiot_cellular_bands',
			'cellular_reconnect_time',
			'metered_flag'
		],
		'networking': [
			'enable_wifi_sta',
			'mobile_master.ssid',
			'mobile_master.password',
			'mobile_master.priority',
			'mobile_network.ssid',
			'mobile_network.password',
			'mobile_network.priority',
			'router_network.ssid',
			'router_network.password',
			'router_network.priority',
			'online_network.ssid',
			'online_network.bssid',
			'online_network.password',
			'online_network.priority',
		],
		'connectivityFrequency': [],
		'billedKWHrsSettings': [
			'charger_efficiency',
			'battery_efficiency',
			'return_factor',
		],
		'maintenanceSchedule': [
			'maintenance_schedule.report_ahr_opt',
			'maintenance_schedule.report_ahr',
			'maintenance_schedule.report_kwhr_opt',
			'maintenance_schedule.report_kwhr',
			'maintenance_schedule.report_usage_hours_opt',
			'maintenance_schedule.report_usage_hours',
			'maintenance_schedule.report_period_opt',
			'maintenance_schedule.report_period',
		],
		'official-reporting': [
		]
	};

	imDeviceSettingsUiStructure = {
		'info': [
			'setup_key',
			'memory_signature',
			'last_saved_user_id',
			'mac_address',
			'serial_number',
			'cm_po_number',
			'last_change_time',
			'meter_sn',
			'last_meter_changetime_temp',
			'subscription_date',
			'installation_date',
			'hw_version',
			'power_view_id',
			'power_view_sn',
			'load_type',
			'in_test_mode',
			'setup_done',
			'setup_reset_data',
			'sct_pre_configured',
			'setup_user_id',
			'setup_time',
			'setup_requested_site',
			'setup_requested_customer',
			'setup_requested_address',
			'setup_replace_mac',
			'fw_version',
			'latitude',
			'longitude',
			'is_rct',
		],
		'deviceSettings': [
			'energy_acc_threshold_outer',
			'energy_acc_threshold_inner',
			'energy_acc_seconds_valid_energy',
			'energy_acc_seconds_no_energy',
			'over_voltage_threshold',
			'under_voltage_threshold',
			'over_current_threshold',
			'modbus_enable',
			'reverse_energy_sign',
			'remote_flash_timer',
			'zone_id',
			'enable_rt',
			'rtLogFrequency',
			'rtLogFrequencyUnit',
			'ct_ratio',
			'_virtual_service_type',
			'voltage_reference',
			'wiring_mode',
			'enabled_wires',
			'ade90xx_settings.sel_freq',
			'ade90xx_settings.ade_type',
		],
		'networking': [
			'enable_wifi_sta',
			'mobile_master.ssid',
			'mobile_master.password',
			'mobile_master.priority',
			'mobile_network.ssid',
			'mobile_network.password',
			'mobile_network.priority',
			'router_network.ssid',
			'router_network.password',
			'router_network.priority',
			'online_network.ssid',
			'online_network.bssid',
			'online_network.password',
			'online_network.priority',
		],
		'networkingSettings': [
			'remoter_server_port',
			'mobile_port',
			'master_port',
			'reconnect_time',
			'remoter_server_ip_options',
			'remoter_server_ip',
			'enable_multicast',
			'multicast_port',
			'multicast_ip',
			'ap_password',
			'ap_ssid',
			'enable_wifi_ap_on_start',
			'ap_channel',
			'enable_serversocket',
			'enc_code',
			'server_socket_port',
			'enable_cellular',
			'cellular_region',
			'static_cellular_ip',
			'static_cellular_gw',
			'static_cellular_netmask',
			'cell_acces_point_name',
			'lte_cellular_bands',
			'nbiot_cellular_bands',
			'cellular_reconnect_time',
			'ip_settings',
			'ip_assignment_ethernet',
			'metered_connection',
		],
		'eventTypeSettings': [
			'event_logic',
			'idle_to_consume_power',
			'idle_to_consume_timer',
			'idle_to_generate_power',
			'idle_to_generate_timer',
			'consume_to_idle_power',
			'consume_to_idle_timer',
			'consume_to_generate_power',
			'consume_to_generate_timer',
			'generate_to_idle_power',
			'generate_to_idle_timer',
			'generate_to_consume_power',
			'generate_to_consume_timer',
		],
		'cal': [
			'ade90xx_settings.v_a_gain',
			'ade90xx_settings.v_b_gain',
			'ade90xx_settings.v_c_gain',
			'ade90xx_settings.i_a_gain',
			'ade90xx_settings.i_b_gain',
			'ade90xx_settings.i_c_gain',
			'ade90xx_settings.i_n_gain',
			'ade90xx_settings.aigain',
			'ade90xx_settings.bigain',
			'ade90xx_settings.cigain',
			'ade90xx_settings.nigain',
			'ade90xx_settings.avgain',
			'ade90xx_settings.bvgain',
			'ade90xx_settings.cvgain',
			'ade90xx_settings.airmsos',
			'ade90xx_settings.birmsos',
			'ade90xx_settings.cirmsos',
			'ade90xx_settings.nirmsos',
			'ade90xx_settings.avrmsos',
			'ade90xx_settings.bvrmsos',
			'ade90xx_settings.cvrmsos',
			'ade90xx_settings.aphcal0',
			'ade90xx_settings.bphcal0',
			'ade90xx_settings.cphcal0',
			'ade90xx_settings.apgain',
			'ade90xx_settings.bpgain',
			'ade90xx_settings.cpgain',
			'ade90xx_settings.aptos',
			'ade90xx_settings.bptos',
			'ade90xx_settings.cptos',
			'ade90xx_settings.aqtos',
			'ade90xx_settings.bqtos',
			'ade90xx_settings.cqtos',
			'ade90xx_settings.var_acc',
			'ade90xx_settings.watt_acc',
			'ade90xx_settings.burden_res',
			'ade90xx_settings.r1',
			'ade90xx_settings.r2',
			'ade90xx_settings.vconsel',
			'ade90xx_settings.iconsel',
		],
	};

	cellularBandsFields = ['lte_cellular_bands', 'nbiot_cellular_bands'];
	cellularIpFields = ['static_cellular_ip', 'static_cellular_gw', 'static_cellular_netmask'];

	waitTimeout = 45;

	deviceWarranty = 1; //In years

	oneDaySeconds: number = 24 * 60 * 60;
	threeDaysDisconnect: number = 3 * (24 * 60 * 60);
	sevenDayDisconnect: number = 7 * (24 * 60 * 60);
	thirtyDayDisconnected: number = 30 * (24 * 60 * 60);
	sixMonths: number = this.oneDaySeconds * 180;

	readonly deviceCommandsResponse = 2;

	readonly rtLogFrequencyMaxValue = 127;
	readonly rt_log_frequency_units = {
		seconds: { value: 0, min: 5 },
		minutes: { value: 1, min: 1 },
	};

	readonly imTypeVoltageLevel = {
		lineToNeutral: 1,
		lineToLine: 0
	};

	readonly maxAllowedIntValue = 2147483647;
	readonly lineToLine = 0;
	readonly lineToNeutral = 1;

	glitchEventTypeMapper: any = {
		'1': 'oia',
		'2': 'oib',
		'3': 'oic',
		'4': 'oin',
		'5': 'dipa',
		'6': 'dipb',
		'7': 'dipc',
		'8': 'swella',
		'9': 'swellb',
		'10': 'swellc',
		// LL
		'133': 'dipa', // 0x85
		'134': 'dipb', // 0x86
		'135': 'dipc', // 0x87
		'136': 'swella', // 0x88
		'137': 'swellb', // 0x89
		'138': 'swellc', // 0x8A
	};

	imDevicesTypeOptions: Array<{ id: number, name: string }> = [
		{id: DeviceFactory.types.ioTEmeterSp1, name: 'IoTEmeter SP1'},
		{id: DeviceFactory.types.ioTEmeterSp3, name: 'IoTEmeter SP3'},
		{id: DeviceFactory.types.ioTEmeter, name: 'IoTEmeter Pro'},
	];

	readonly eventLogic = {
		daily: 0,
		stateBased: 1,
	};

	readonly imPowerThresholdFields = [
		'idle_to_consume_power',
		'idle_to_generate_power',
		'consume_to_idle_power',
		'consume_to_generate_power',
		'generate_to_idle_power',
		'generate_to_consume_power',
	];

	// The threshold a state is entered on must sit at or above the threshold that same state is left on, so no
	// single reading can satisfy both directions at once and leave the device flipping between two states.
	// Consume to Generate and Generate to Consume take part in no comparison: the firmware reads them as
	// independent values.
	// When you change these rules, change the matching rules in the backend (Common/validator.js).
	readonly imPowerThresholdOrderRules = [
		{ field: 'idle_to_consume_power', atLeast: 'consume_to_idle_power' },
		{ field: 'idle_to_generate_power', atLeast: 'generate_to_idle_power' },
	];

	// Firmware earlier than this does not report geoFenceStatus at all, so the Device Info row reads
	// "Not Supported" rather than a status the device never sent.
	readonly geofenceStatusMinFwVersion = 2.5110;

	// The device reports its own geofence state in the Mini Define. This is a different source from the
	// "Outside Geofence" exception reports, which the server works out from the site geofence distance.
	readonly geofenceStatusLabels = {
		0: 'devices.geofence_disabled_unknown',
		1: 'devices.geofence_outside',
		2: 'devices.geofence_inside',
	};

	/**
	 * The text and badge colour for the Geofence Status row on Device Info.
	 * Anything the firmware does not define reads "Unknown" rather than a raw number.
	 */
	getGeofenceStatusDisplay(device: any): {text: string, badgeClass: string} {
		// Written as a failed >= rather than a < so a missing or unreadable fw_version, which reads NaN,
		// falls to "Not Supported" instead of claiming a status the device may never have sent.
		if (!device || !(+device.fw_version >= this.geofenceStatusMinFwVersion))
			return {text: this.translateService.instant('devices.geofence_not_supported'), badgeClass: 'badge-secondary'};

		const labelKey = this.geofenceStatusLabels[device.geo_fence_status];
		if (!labelKey)
			return {text: this.translateService.instant('devices.geofence_unknown'), badgeClass: 'badge-secondary'};

		const badgeClasses = {0: 'badge-secondary', 1: 'badge-danger', 2: 'badge-success'};
		return {text: this.translateService.instant(labelKey), badgeClass: badgeClasses[device.geo_fence_status]};
	}

	constructor(
		private httpClient: HttpClient,
		private translateService: TranslateService,
		private commonUtil: CommonService,
		private usersService: UsersService,
		private validationService: ValidationService,
		private deviceCommonService: DeviceCommonService,
		private deviceTypeService: DeviceTypeService
	) { }

	formatDeletedMACandSN(text: string){
		let res = this.translateService.instant('g.deleted')+': ';
		let tmp1 = text.substr(3);
		let tmp2 = tmp1.split('-');
		res += tmp2[0] + ' (' + moment(+tmp2[1] * 1000).utc().format('MM/DD/YYYY hh:mm:ss A') + ')'
		return res;
	}

	updateDeviceConfig(data) {
		this.updateConfigDataSbj.next({isDone : true, data: data})
	}

	formatDeviceFields(device) {
		for(let field of this.deviceDateFields) {
			if(device[field] !== undefined) {
				device[field+'_fmt'] = moment.utc(device[field]*1000).format('MM/DD/YYYY');
			}
		}
		for(let field of this.deviceTimeReferenceFields) {
			if(device[field] !== undefined) {
				device[field+'_fmt'] = `${moment(device[field]*1000).format('MM/DD/YYYY hh:mm:ss A')} (${this.commonUtil.showUserTimeZoneReference()})`;
			}
		}
	}

	formatWifiAuthMode(authMode) {
		let mode = '-';
		switch(authMode) {
			case 0: mode = this.wifiAuthMode.open; break;
			case 1: mode = this.wifiAuthMode.wep; break;
			case 2: mode = this.wifiAuthMode.psk; break;
			case 3: mode = this.wifiAuthMode.wpa2_psk; break;
			case 4: mode = this.wifiAuthMode.wpa_wpa2_psk; break;
			case 5: mode = this.wifiAuthMode.wpa2_enterprise; break;
		}
		return mode;
	}

	formatWifiSignalStrength(rssi) {
		let rssiMessage;
		if(rssi >= -60)
			rssiMessage = this.translateService.instant('g.excellent');
		else if (rssi >= -72)
			rssiMessage = this.translateService.instant('g.very_good');
		else if (rssi >= -75)
			rssiMessage = this.translateService.instant('g.okay');
		else if (rssi >= -85)
			rssiMessage = this.translateService.instant('g.weak');
		else
			rssiMessage = this.translateService.instant('g.unusable');
		return rssiMessage;
	}

	formatWifiNetworks(networks) {
		networks = networks || [];
		networks.forEach((network) => {
			network.signalStrength = this.formatWifiSignalStrength(network.rssi) + '('+network.rssi+')';
			network.authMode = this.formatWifiAuthMode(network.auth_mode);
		});
		return networks;
	}

	isNewDevice(year) {
		return !year || year == 0 || year >= 2010;
	}

	// Battery type of BATTlink-family devices comes from the device type, not the stored battery_type setting
	getBatteryType(device) {
		switch (+device.sct_device_type) {
			case DeviceFactory.types.lithiumBattlink:
				return this.batteryTypesByName.lithuim_ion;
			case DeviceFactory.types.battlink:
			case DeviceFactory.types.battlinkII:
				return this.batteryTypesByName.lead;
			default:
				return device.battery_type;
		}
	}

	calculateBilledKWHr(totalKWHr, device) {
		if(device.return_factor && device.charger_efficiency && device.battery_efficiency)
			return (totalKWHr * device.return_factor / (device.charger_efficiency*device.battery_efficiency));
		return;
	}

	calculateCO2(billedKwhr) {
		return this.commonUtil.lbToMt(billedKwhr * 7.03 * 2204.62 / 10000);
	}

	validateLastPMDate(lastPMDate, installationDate) {
		let invalidFields = this.commonUtil.validateDeviceSettings({last_pm_date: lastPMDate});
		return invalidFields.length == 0 && lastPMDate >= moment(installationDate*1000).startOf('day').unix() && lastPMDate <= moment().endOf('day').unix();
	}

	mapRunTimeWarning(warning: number) {
		const warningsArr = []
		if ((warning & 0x00000001) != 0)
			warningsArr.push('negative_consumed_energy_check');

		if ((warning & 0x00000002) != 0)
			warningsArr.push('negative_generated_energy_check');

		return warningsArr;
	}

	getDeviceInfo(mac_address, options?) {
		return this.httpClient.post(this.route + 'getDeviceInfo', {mac_address, options}, {
			observe: "body"
		});
	}

	getDevicesInfoBySerialNumber(serialsArray: string[]) {
		return this.httpClient.post(this.route + 'getDevicesInfoBySerialNumber', {serialsArray}, {
			observe: "body"
		});
	}
	getDisableExternalRtcDevices() {
		return this.httpClient.post(this.route + 'getDisableExternalRtcDevices', {}, {
			observe: "body"
		});
	}

	getDisableXTRAFileDevices() {
		return this.httpClient.post(this.route + 'getDisableXTRAFileDevices', {}, {
			observe: "body"
		});
	}

	enableExternalRTC(macAddresses) {
		return this.httpClient.post(this.route + 'enableExternalRTC', {macAddresses}, {
			observe: "body"
		});
	}

	enableExtraFile(macAddresses) {
		return this.httpClient.post(this.route + 'enableExtraFile', {macAddresses}, {
			observe: "body"
		});
	}

	getDisconnectedDevicesReport(options) {
		return this.httpClient.post(this.route + 'getDisconnectedDevicesReport', { options }, {
			observe: "body"
		});
	}

	getDisconnectedSitesReport(filterOptions: {percentage: number, daysCount: number, siteStateFilter: string[], isSmartRebatesSitesFilter: boolean}) {
		return this.httpClient.post('/reporting/getDisconnectedSitesReport', { filterOptions }, {
			observe: "body"
		});
	}

	getDisconnectedSitesReportAsPDF(filterOptions: { percentage: number, daysCount: number, siteStateFilter: string[], isSmartRebatesSitesFilter: boolean }) {
		return this.httpClient.post('/reporting/getDisconnectedSitesReportAsPDF', { filterOptions }, {
			observe: "body"
		});
	}

	getMultiDevicesInfo(deviceIds) {
		return this.httpClient.post(this.route + 'getMultiDevicesInfo', {deviceIds}, {
			observe: "body"
		});
	}

	getWarnings(filter: any) {
		return this.httpClient.post(this.route + 'getWarnings', {filter}, {
			observe: "body"
		});
	}

	getCurrentWarningsList(mac_address: string) {
		return this.httpClient.get(this.route + 'getCurrentWarningsList', {
			params: { mac_address },
			observe: "body"
		});
	}

	acknowledgeWarnings(warning_id: string, mac_address: string) {
		return this.httpClient.post(this.route + 'acknowledgeWarnings', {warning_id, mac_address}, {
			observe: "body"
		});
	}

	getQuickView(mac_address, noUiBlock) {
		return this.httpClient.post(this.route + 'getQuickView', {mac_address}, {
			observe: "body",
			params: new CustomHttpParams({noUIBlock: noUiBlock})
		});
	}

	getEvents(mac_address, fromTime, toTime, studyId=null) {
		return this.httpClient.post(this.route + 'getEvents', {mac_address, fromTime, toTime, studyId}, {
			observe: "body"
		});
	}

	getIncorrectEvents(options: { year: number, quarter: number, sct_device_type: number }) {
		return this.httpClient.get(this.route + 'get-incorrect-events', {
			observe: "body",
			params: options,
		});
	}

	getDevicesUsageTime(deviceId: string[], siteId: number, startTime: number, endTime: number, {truckYear, truckType, truckTag, parentPage}) {
		return this.httpClient.post(this.route + 'get-devices-usage-time', {deviceId, siteId, startTime, endTime, truckYear, truckType, truckTag, parentPage}, {
			observe: "body",
		});
	}

	getUsageChart(mac_address: string, options: {is_daily: boolean, year: number, siteId: number, studyId?: number, sct_devices_types?: number[]}) {
		return this.httpClient.post(this.route + 'getUsageChart', {mac_address, options}, {
			observe: "body"
		});
	}

	getDeviceSOCData(deviceId: string, studyId: number, startTime: number, endTime: number, workingDaysOnly: boolean = false, getVPCChartsData: boolean = false) {
		return this.httpClient.post(this.route + 'get-device-soc', {deviceId, studyId, startTime, endTime, workingDaysOnly, getVPCChartsData }, {
			observe: "body",
		});
	}

	getDeviceChargeCycles(deviceId: string, studyId: number, startTime: number, endTime: number) {
		return this.httpClient.post(this.route + 'get-device-charge-cycles', {deviceId, studyId, startTime, endTime}, {
			observe: "body",
		});
	}

	queueExportEventsRequest(selectedColumnsFields: string[], macAddressesList: number[], dateRange: {to: number, from: number}, siteId: number) {
		return this.httpClient.post(this.route + 'queueExportEventsRequest', {selectedColumnsFields, macAddressesList, dateRange, siteId}, {
			observe: "body"
		});
	}

	getDailyDetails(mac_address, fromTime, toTime, options?, studyId=0) {
		return this.httpClient.post(this.route + 'getDailyDetails', {mac_address, fromTime, toTime, studyId, options}, {
			observe: "body"
		});
	}

	sendCommand(mac_address, command, options?, blockUI = true) {
		return this.httpClient.post(this.route + 'sendAction/' + command, {mac_address, options, wait: (['quickView', 'queryCellularInfo'].includes(command) ? 0 : this.waitTimeout)}, {
			observe: "body",
			params: new CustomHttpParams({noUIBlock: !blockUI})
		});
	}

	getRTs(mac_address, fromTime) {
		return this.httpClient.post(this.route + 'getRT', {mac_address, fromTime}, {
			observe: "body"
		});
	}

	fetchRtRecordsByRange(mac_address, range: {fromTime?: string, toTime?: string, fromId?: number, toId?: number}) {
		return this.httpClient.post(this.route + 'fetchRtRecordsByRange', {mac_address, range}, {
			observe: "body"
		});
	}

	getDeviceConfigTrack(id, options) {
		return this.httpClient.post(this.route + 'getDeviceConfigTrack', {id, options}, {
			observe: "body"
		});
	}

	getCellularConfigTrack(mac_address: string, options) {
		return this.httpClient.post(this.route + 'getCellularConfigTrack', {mac_address, options}, {
			observe: "body"
		});
	}

	getConnectivityTrack(mac_address, fromDate, toDate) {
		return this.httpClient.get(this.route + 'getConnectivityTrack', {
			params: { mac_address, fromDate: fromDate.toString(), toDate: toDate.toString() },
			observe: "body"
		});
	}

	getGlitches(mac_address, fromDate, toDate) {
		return this.httpClient.get(this.route + 'glitch', {
			params: { macAddress: mac_address, fromTime: fromDate.toString(), toTime: toDate.toString() },
			observe: "body"
		});
	}

	getDeviceActions(mac_address, fromDate, toDate, options) {
		return this.httpClient.post(this.route + 'getDeviceActions', {mac_address, fromDate, toDate, options}, {
			observe: "body"
		});
	}

	getCellularInfo(mac_address) {
		return this.httpClient.get(this.route + 'getCellularInfo', {
			params: { mac_address },
			observe: "body"
		});
	}

	getWarningsMabValues(warningKey: string, options: { deviceType?: number } = {}) {
		const objectMappers = this.commonUtil.getObjectMapper(options.deviceType);
		const mapped = objectMappers['warnings'];
		const value = Object.keys(mapped).filter((key) => mapped[key] == warningKey);
		return value[0];
	}

	getWarningsList(options: { deviceType?: number } = {}) {
		return this.getWarningsKeys(options).map(warning => {
			return {key: warning, value: this.translateService.instant(`warnings.${warning}`)}
		});
	}

	getWarningsKeys(options: { deviceType?: number } = {}) {
		const objectMappers = this.commonUtil.getObjectMapper(options.deviceType);
		return Object.keys(objectMappers['warnings']);
	}

	filterWarningsByPermission(warningsList, permission, options = {canIgnorePermission: false}) {
		if (!warningsList?.length)
			return [];

		const filteredWarnings = [];

		let ignorePermissionList = this.excludePermissionWarnings();
		for(let warning of warningsList) {
			if(options.canIgnorePermission && (ignorePermissionList.includes(warning)) || this.usersService.hasAccessPermission({permission}, 'warnings.'+warning))
				filteredWarnings.push(warning);
		}
		return filteredWarnings;
	}

	getWarningOptionsList() {
		return [
			{id: 'current_time', value: this.translateService.instant('warnings.current_time')},
			{id: 'current_sensor_open', value: this.translateService.instant('warnings.current_sensor_open')},
			{id: 'voltage_error_calibration', value: this.translateService.instant('warnings.voltage_error_calibration')},
			{id: 'voltage_error_value', value: this.translateService.instant('warnings.voltage_error_value')},
			{id: 'external_temperature_error', value: this.translateService.instant('warnings.external_temperature_error')},
			{id: 'current_error_calibration', value: this.translateService.instant('warnings.current_error_calibration')},
			{id: 'mis_voltage', value: this.translateService.instant('warnings.mis_voltage')},
			{id: 'mis_capacity', value: this.translateService.instant('warnings.mis_capacity')},
			{id: 'no_setup', value: this.translateService.instant('warnings.no_setup')}
		]
	}

	/**
	 * Return warnings to exclude permission check for (Device warnings; device.device_warnings)
	 * Used in device listings
	 */
	excludePermissionWarnings() {
		return [
			'current_time',
			'current_sensor_open',
			'voltage_error_calibration',
			'voltage_error_value',
			'external_temperature_error',
			'current_error_calibration',
		];
	}

	/**
	 * Parse IM device warnings live, from the device's current fields — not from a warnings table.
	 * device_warnings and hw_status are decoded per device type by the shared
	 * Common/devices-config/imDeviceWarningsDecoder.js (the "IOTEMETER Full permissions" sheet), the
	 * same decoder Shipment Quality uses on the backend.
	 */
	getIMDeviceWarnings(device, options: { includePassword?: boolean } = {}): string[] {
		const qv = this.commonUtil.decompress(device.quick_view || {}, 'quick_view', device.sct_device_type);
		const warnings = imDeviceWarningsDecoder.decodeIMDeviceWarnings(device, qv);

		const runtimeWarnings = qv.live_rt?.runtime_warnings;
		if (runtimeWarnings) {
			if ((runtimeWarnings & 0x00000001) !== 0)
				warnings.push('negative_consumed_energy_check');
			if ((runtimeWarnings & 0x00000002) !== 0)
				warnings.push('negative_generated_energy_check');
		}

		if (device.lost_rtc)
			warnings.push('lost_rtc');
		if (device.has_rtc_time_drift_warning === true)
			warnings.push('rtc_time_drift_warning');

		// setup_network_on is stored uncompressed under its own name in quick_view, unlike
		// hw_status/live_rt above — decompress passes unmapped keys through as-is.
		if (qv.setup_network_on)
			warnings.push('setup_network_on');
		if (options.includePassword && !device.device_password)
			warnings.push('password');
		if (device.setup_done !== undefined && !device.setup_done)
			warnings.push('no_setup');
		if (device.setup_done === false && device.sct_pre_configured === false)
			warnings.push('device_not_setup');
		if (device.has_missed_up_events)
			warnings.push('has_missed_up_events');
		if (device.missing_event_ids && device.missing_event_ids.length)
			warnings.push('missing_event_ids');

		return warnings;
	}

	formatQueuedChanges(device, changesStackOrigin, mode='normal', siteZoneId) {
		if(Object.keys(changesStackOrigin).length == 0)
			return changesStackOrigin;

		let changesStack = lodash.cloneDeep(changesStackOrigin);

		var daysMask			= []
		var dates				= ['installation_date', 'setup_time'];
		var times				= [];
		var temperatureFields	= [];
		var booleans			= [];
		var noYesFields			= [];
		var invertedBooleans	= [];

		var daysOfTheWeek	= this.commonUtil.getDropDownListData('week');
		var disableEnable	= [this.translateService.instant('g.disable'), this.translateService.instant('g.enable')];
		var noYes			= [this.translateService.instant('g.no'), this.translateService.instant('g.yes')];
		var invalidOptionText = this.translateService.instant('g.invalid_option')+'!';

		for (var field in changesStack) {

			if(mode == 'plainObject')
				changesStack[field] = [[changesStack[field]]];

			if(temperatureFields.indexOf(field) > -1) {

				if(device.temperatureformat) {
					changesStack[field].forEach((change) => {
						change[0] = Math.round(change[0] * 1.8 + 32);
					});
				}
			} else if(dates.indexOf(field) > -1) {

				changesStack[field].forEach((change) => {
					let timeInSiteTimezone = this.commonUtil.getZoneTimestampFromUTC(siteZoneId, change[0]);
					change[0] = moment(timeInSiteTimezone * 1000).utc().format('MM/DD/YYYY');
				});
			} else if(times.indexOf(field) > -1) {

				changesStack[field].forEach((change) => {
					let timeInSiteTimezone = this.commonUtil.getZoneTimestampFromUTC(siteZoneId, change[0]);
					change[0] = moment(timeInSiteTimezone * 1000).utc().format('MM/DD/YYYY hh:mm:ss a');
				});
			} else if(invertedBooleans.indexOf(field) > -1) {

				changesStack[field].forEach((change) => {
					change[0] = +!change[0];
					change[0] = disableEnable[change[0]];
					if(!change[0])
						change[0] = invalidOptionText;
				});
			} else if(booleans.indexOf(field) > -1) {

				changesStack[field].forEach((change) => {
					change[0] = +change[0];
					change[0] = disableEnable[change[0]];
					if(!change[0])
						change[0] = invalidOptionText;
				});
			} else if(daysMask.indexOf(field) > -1) {

				changesStack[field].forEach((change) => {
					if(typeof change[0] == 'object' && change[0].length > 0) {
						var dayNames = [];
						change[0].forEach((day) => {
							if(daysOfTheWeek[day])
								dayNames.push(daysOfTheWeek[day].text);
							else
								dayNames.push(this.translateService.instant('g.invalid_day')+'!');
						});
						change[0] = dayNames.join(', ');
					}
				});
			} else if(noYesFields.indexOf(field) > -1) {
				changesStack[field].forEach((change) => {
					change[0] = +change[0];
					change[0] = noYes[change[0]];
					if(!change[0])
						change[0] = invalidOptionText;
				});
			}
		}

		if(mode == 'plainObject') {

			let toReturn = {};
			for (var field in changesStack) {
				toReturn[field] = changesStack[field][0][0];
				switch (field) {
					case "last_saved_user_id":
						if(toReturn[field] == 0xFFFFFFFF)
							toReturn[field] = this.translateService.instant('g.system');
					break;
					case "last_change_time":
						let timeInSiteTimezone = this.commonUtil.getZoneTimestampFromUTC(siteZoneId, toReturn[field]);
						toReturn[field] = moment(timeInSiteTimezone * 1000).utc().format('MM/DD/YYYY hh:mm:ss A');
					break;
					case "last_pm_date":
						toReturn[field] = moment(toReturn[field] * 1000).utc().format('MM/DD/YYYY');
					break;
				}
			}
			return toReturn;
		}

		return changesStack;
	}

	getDebugRecords(mac_address, options) {
		return this.httpClient.post(this.route + 'getDebugRecords', {mac_address, options}, {
			observe: "body"
		});
	}

	exportDebugRecordsCSV(mac_address, options) {
		return this.httpClient.post(this.route + 'exportDebugRecordsCSV', {mac_address, options}, {
			observe: "body"
		});
	}

	getDeviceFirmwareVersions() {
		return this.httpClient.get(this.route + 'getDeviceFirmwareVersions', {
			observe: "body"
		});
	}

	pushFirmwareUpdate(deviceID, newFwVersion, type) {
		return this.httpClient.post(this.route + 'pushFirmwareUpdate', {deviceID, newFwVersion, type}, {
			observe: "body"
		});
	}

	saveDeviceSettings(mac_address, configs, stage, options?, wait = 0) {
		return this.httpClient.patch(this.route + 'saveDeviceSettings', {mac_address, configs, stage, options, wait}, {
			observe: "body"
		});
	}

	saveMultiDevicesSettings(deviceIds, configs, eachDeviceConfig = null) {
		return this.httpClient.patch(this.route + 'saveMultiDevicesSettings', {deviceIds, configs, eachDeviceConfig}, {
			observe: "body"
		});
	}

	getSiteDevicesListing(siteID, getSiteInfo = false, noUIBlock = false) {
		return this.httpClient.post(this.route + 'getSiteDevicesListing', {siteID, getSiteInfo}, {
			observe: "body",
			params: new CustomHttpParams({noUIBlock})
		});
	}

	fetchSiteDevicesList(siteID: number, studyId = 0) {
		return this.httpClient.get(this.route + 'fetchSiteDevicesList', {
			params: { siteID: siteID.toString(), studyId: studyId.toString() },
			observe: "body"
		});
	}

	getAllSitesDevicesListing() {
		return this.httpClient.post(this.route + 'getAllSitesDevicesListing', {}, {
			observe: "body"
		});
	}

	getBlockedFWReport() {
		return this.httpClient.get(this.route + 'getBlockedFWReport', {
			observe: "body"
		});
	}

	unBlockFWReport(devices: {mac_address: string, version: string}[]) {
		return this.httpClient.post(this.route + 'unBlockFWReport', {devices}, {
			observe: "body"
		});
	}

	unblockDeviceConnection(mac_address: string) {
		return this.httpClient.post(this.route + 'unblockDeviceConnection', {mac_address}, {
			observe: "body"
		});
	}

	getBlockedDevices() {
		return this.httpClient.get(this.route + 'blocked-devices', {
			observe: "body"
		});
	}

	unblockDevicesConnection(mac_addresses: string[]) {
		return this.httpClient.post(this.route + 'unblock-devices-connection', {mac_addresses}, {
			observe: "body"
		});
	}

	setLastMaintenanceDate(macAddress, lastPMDate) {
		return this.httpClient.post(this.route + 'setLastMaintenanceDate', {macAddress, lastPMDate}, {
			observe: "body"
		});
	}

	addDeviceToWakeUpList(mac_address: string, iccid: string, remoter_server_port: number) {
		return this.httpClient.post(`cellular/addDeviceToWakeUpList`, {mac_address, iccid, remoter_server_port}, {observe:"body"});
	}

	setMultiLastMaintenanceDate(deviceIds, lastPMDate) {
		return this.httpClient.post(this.route + 'setMultiLastMaintenanceDate', {deviceIds, lastPMDate}, {
			observe: "body"
		});
	}

	addConnectivityClasses(nowTime, devices) {
		for (var item of devices) {
			const installatioDate = item.installation_date + this.oneDaySeconds;
			item.tooltipMessages = item.tooltipMessages || [];
			const lastConnectTime = nowTime - item.last_connect_time;
			// WIFI
			let wifiLastConnectTime = lodash.max([item.non_metered_wifi_last_connect_time, item.metered_wifi_last_connect_time]);
			let wifiDisconnectTime = nowTime - wifiLastConnectTime;
			this.updateConnectivityFlag(lastConnectTime, item);
			let hasWifiImage = false;
			if (wifiLastConnectTime > installatioDate && wifiDisconnectTime < this.sixMonths) {
				hasWifiImage = true;
				this.showWifiImage(wifiDisconnectTime, item);
				item.wifiTooltipMessage = this.getLastConnectionTimeMassage(item, wifiLastConnectTime, nowTime);
			}

			// LTE
			const lteLastConnectTime = lodash.max([item.non_metered_cellular_last_connect_time, item.metered_cellular_last_connect_time]);
			const lteDisconnectTime = nowTime - lteLastConnectTime;
			let hasLteImage = false;
			if (lteLastConnectTime > installatioDate && lteDisconnectTime < this.sixMonths) {
				hasLteImage = true;
				this.showLteImage(nowTime, lteDisconnectTime, item);
			}

			if (!hasWifiImage && !hasLteImage) {
				wifiLastConnectTime = item.last_connect_time;
				wifiDisconnectTime = nowTime - wifiLastConnectTime;
				if(lteDisconnectTime > wifiDisconnectTime) {
					this.showLteImage(nowTime, lteDisconnectTime, item)
				} else {
					this.showWifiImage(wifiDisconnectTime, item);
					item.wifiTooltipMessage = this.getLastConnectionTimeMassage(item, wifiLastConnectTime, nowTime);
				}
			}

			if (lastConnectTime == null)
				continue;

			// ST-2712: the card shows the newer of the general and the Wi-Fi last connect time,
			// so it never reads older than the Wi-Fi icon.
			const cardLastConnectTime = lodash.max([
				item.last_connect_time, item.non_metered_wifi_last_connect_time, item.metered_wifi_last_connect_time,
			]);
			const cardConnectTime = nowTime - cardLastConnectTime;

			if (cardConnectTime < 60) // less than 1 min
				item.tooltipMessages.push(`${this.translateService.instant('devices.last_connect')}: ${this.translateService.instant('devices.less_than_a_min')}`);

			else if(this.commonUtil.secondsToElapsedTime(cardConnectTime))
				item.tooltipMessages.push(this.translateService.instant('devices.last_connect') + ': ' + this.commonUtil.secondsToElapsedTime(cardConnectTime));
		}
		return devices;
	}

	// connectTime / nowTime default to the generic last connect time and the browser clock.
	getLastConnectionTimeMassage(device: any, connectTime = device.last_connect_time, nowTime = this.commonUtil.nowTime()) {
		let lastConnectTime = nowTime - connectTime;

		if (!lastConnectTime)
			return '';

		if (typeof lastConnectTime !== 'number')
			lastConnectTime = moment.utc(lastConnectTime).unix();

		if (lastConnectTime < 60) // less than 1 min
				return `${this.translateService.instant('devices.last_connect')}: ${this.translateService.instant('devices.less_than_a_min')}`;

		return this.translateService.instant('devices.last_connect') + ': ' + this.commonUtil.secondsToElapsedTime(lastConnectTime) ;
	}

	showLteImage(nowTime, lastConnectTime, item) {
		// if a device cellular enabled  = false → hide the icon
		if (item.enable_cellular === false)
			return;

		// if the device doesn’t have ICCID and the user doesn’t have NOC permission → hide the icon
		if (!item.cellular_iccid) {
			if (!this.usersService.userHasNOCAccess())
				return;

			// if the user has NOC permission and the device doesn’t have ICCID show the lte_bad
			item.connectivityStatus = '/images/lte/lte_BAD.png';
			return;
		}

		if (lastConnectTime >= this.thirtyDayDisconnected) {  // not connected for 30 days
			item.connectivityStatus = '/images/lte/lte_disconnected_30d.png';
			return;
		}
		if (lastConnectTime >= this.sevenDayDisconnect) {  // not connected for 7 days
			item.connectivityStatus = '/images/lte/lte_disconnected_7d.png';
			return;
		}
		if (lastConnectTime >= this.threeDaysDisconnect) { // not connected for 3 days
			item.connectivityStatus = '/images/lte/lte_disconnected_3d.png';
			return;
		}
		if (lastConnectTime >= this.oneDaySeconds) {  // connected for one day and lessthan 3 days check SIM time DB
			const lastSimTime = nowTime - item.last_sim_connect_time;
			if (lastSimTime < this.oneDaySeconds) {
				item.connectivityStatus = '/images/lte/lte_connected.png';
				return;
			}
			item.connectivityStatus = '/images/lte/lte_disconnected_3d.png';
			return;
		}
		item.connectivityStatus = '/images/lte/lte_connected.png';
		return;
	}

	showWifiImage(disconnectTime, item) {
		let connectFrequency = item.reconnect_time;
		item.tooltipMessages = [];
		if (disconnectTime <= (2.5 * connectFrequency)) {
			//Less than 2.5 times of normal connectFrequency
			item.wifiConnectivityStatus = '/images/wifi_connected.png';
		} else {
			if(disconnectTime <= 8*24*3600) {
				//one day
				item.wifiConnectivityStatus = '/images/wifi_not_connected_1d.png';
			} else if(disconnectTime > 8*24*3600 && disconnectTime <= 30*24*3600) {
				//More than 8 days hrs and less than 30 days
				item.wifiConnectivityStatus = '/images/wifi_not_connected_7d_orange.png';
			} else {
				item.wifiConnectivityStatus = '/images/wifi_not_connected_red.png';
			}
		}
	}

	updateConnectivityFlag(disconnectTime, item ) {
		let connectFrequency = item.reconnect_time;
		item.connectivityFlag = 0;
		if (disconnectTime <= (2.5 * connectFrequency)) {
			//Less than 2.5 times of normal connectFrequency
			item.connectivityFlag	= 1;
		} else {
			if(disconnectTime <= 24*3600) {
				// one day
				item.connectivityFlag	= 2;
			} else if(disconnectTime > 24*3600 && disconnectTime <= 7*24*3600) {
				// one week
				item.connectivityFlag	= 3;
			} else {
				// more than one week
				item.connectivityFlag	= 4;
			}
		}
	}

	getImageRouterLink(type: string, customerId: number, device: any) {
		const baseImageUrl = ['/', customerId, device.site_id, device.mac_address];

		if (type == 'cellular' && this.usersService.hasAccessPermission(null, 'debug_data')) {
			return [...baseImageUrl, 'cellular-settings'];
		}

		if (type == 'wifi' && this.usersService.hasAccessPermission(null, 'networking_admin')
			|| this.usersService.hasAccessPermission(null, 'wifi_admin')
			|| this.usersService.hasAccessPermission(null, 'basic_setup')) {
			return [...baseImageUrl, 'settings'];
		}
		return null;
	}

	formatChangesStackField(value, type, options?) {
		switch(type) {
			case 'date':
				const zoneDiff = new Date().getTimezoneOffset();
				if(value !== undefined) {
					value += (zoneDiff * 60);
					value = moment(value*1000).format('MM/DD/YYYY');
				}
			break;
			case 'select':
				value = this.deviceFieldsLists[options.field][value];
			break;
			case 'zone':
				value = this.commonUtil.getZoneName(value);
			break;
		}
		return value;
	}

	formatChangesStack(stack, zoneId, device: any = {}) {

		const formatBooleans = (value, trueFalse=false, isIMDevice=false) => {
			if(trueFalse)
				value = +value;

			if (isIMDevice)
				value = value ? 1 : 0;

			value = disableEnable[value];
			if(!value)
				value = this.translateService.instant('g.invalid_input');
			return value;
		};

		device = device.config_info ?? device;
		const isIMDevice = DeviceFactory.isIMDeviceType(device.sct_device_type);
		const booleanFields = this.deviceBooleanFields.slice();

		const disableEnable = [
			this.translateService.instant('g.disable'),
			this.translateService.instant('g.enable')
		];

		for(let field in stack) {
			if(booleanFields.includes(field)) {
				stack[field].forEach((change) => {
					change[0] = formatBooleans(change[0], false, isIMDevice);
				});
			} else if(this.objectFields.includes(field)) {
				stack[field] = this.formatChangesStack(stack[field], zoneId, device);
			} else if(this.deviceDateFields.includes(field)) {
				stack[field].forEach((change) => {
					change[0] = this.formatChangesStackField(change[0], 'date', {zoneId});
				});
			} else if(this.deviceFieldsLists[field]) {
				stack[field].forEach((change) => {
					change[0] = this.formatChangesStackField(change[0], 'select', {field});
				});
			} else if(this.deviceZoneFields.includes(field)) {
				stack[field].forEach((change) => {
					change[0] = this.formatChangesStackField(change[0], 'zone');
				});
			} else if(field == 'rt_log_frequency') {
				for (let index in stack[field]) {
					let rtLogFrequency = stack[field][index][0];
					if (typeof rtLogFrequency == 'number') {
						stack['rtLogFrequency'] = stack[field];
						stack[field][index][0] = this.formatRtLogFrequencyField(device, rtLogFrequency, true);
					}
				}
			} else if (field == 'voltage_reference') {
				for (const index in stack[field]) {
					const voltageReferenceValue = stack[field][index][0];

					if (typeof voltageReferenceValue != 'number')
						continue;

					const value: any = Object.values(this.deviceCommonService.ddLists.voltageReferenceOptions).find((field: any) => field.value == voltageReferenceValue);
					stack[field][index][0] = value.text;
				}
			} else if (['under_voltage_threshold', 'over_voltage_threshold'].includes(field) && device.voltage_reference === this.lineToLine) {
				for (const index in stack[field]) {
					const value = stack[field][index][0];
					stack[field][index][0] = this.commonUtil.calcVoltageReference(value, 'multi').toFixed(2);
				}
			} else if (field == 'ct_ratio') {
				const ctRationValues = device.is_rct ? this.deviceCommonService.ddLists.ctRatioRogowskiType : this.deviceCommonService.ddLists.ctRatioOptions;
				for (const index in stack[field]) {
					const ctRatioValue = stack[field][index][0];
					const value: any = ctRationValues.find(option => option.id === ctRatioValue)?.text || null;
					stack[field][index][0] = value;
				}
			} else if (field == 'wiring_mode') {
				for (const index in stack[field]) {
					const ctRatioValue = stack[field][index][0];
					const value: any = Object.values(this.deviceCommonService.ddLists.wiringModeSettings).find((field: any) => field.value == ctRatioValue);
					stack[field][index][0] = value.text;
				}
			} else if (this.cellularIpFields.includes(field)) {
				for (const index in stack[field]) {
					const value = stack[field][index][0];
					stack[field][index][0] = this.commonUtil.int2ip(value);
				}
			} else if (isIMDevice && field == 'sct_device_type') {
				for (const index in stack[field]) {
					const value = stack[field][index][0];
					const deviceType = this.imDevicesTypeOptions.find(imDevice => imDevice.id == value)?.name || '';
					stack[field][index][0] = deviceType;
				}
			} else if (field == 'truck_manufacturing_year') {
				for (const index in stack[field]) {
					const value = stack[field][index][0];
					stack[field][index][0] = this.commonUtil.getManufacturingYear(value);
				}
			}
		}

		// the CHARGlink Charger Manufacturer shows as the truck_manufacturer_option dropdown, it shows the same queued changes (ST-2746)
		if (stack.truck_manufacturer)
			stack.truck_manufacturer_option = stack.truck_manufacturer;

		return stack;
	}

	formatRtLogFrequencyField(device: any, rtLogFrequency: number, getResultText: boolean) {
		const rtLogFrequencyBin = this.validationService.decimal2binary(rtLogFrequency, 8);
		const rtLogFrequencyUnit = +rtLogFrequencyBin[0];
		const rt_log_frequency = this.validationService.binary2decimal(rtLogFrequencyBin.slice(1));

		if (getResultText)
			return `${rt_log_frequency} ${rtLogFrequencyUnit ? this.translateService.instant('g.minutes_value') : this.translateService.instant('g.seconds_value')}`;

		device.rtLogFrequency = rt_log_frequency;
		device.rtLogFrequencyUnit = rtLogFrequencyUnit;

		device.rtLogFrequency_fmt = structuredClone(rt_log_frequency);
		device.rtLogFrequencyUnit_fmt = structuredClone(rtLogFrequencyUnit);
		return;
	}

	getBatteryRemainingLifeData(device) {
		const batteryMaxLifeYears = device.charger_type && device.charger_type == this.chargerTypes.Fast ? 4 : 5;
		const batteryMaxLifeMonths = batteryMaxLifeYears * 12;

		const manufacturingYear = device.truck_manufacturing_year;
		const manufacturingMonth = device.truck_manufacturing_month || 1;

		const now = new Date();
		const currentYear = now.getFullYear();
		const currentMonth = now.getMonth() + 1;

		const manufacturingTotalMonths = manufacturingYear * 12 + (manufacturingMonth - 1);

		const currentTotalMonths = currentYear * 12 + (currentMonth - 1);

		const monthsUsed = currentTotalMonths - manufacturingTotalMonths;

		let percentLifeRemainingYears = batteryMaxLifeMonths ? ((batteryMaxLifeMonths - monthsUsed) * 100) / batteryMaxLifeMonths : 0;
		percentLifeRemainingYears = Math.max(0, percentLifeRemainingYears);

		const batteryMaxLifeAh = 1250 * (device.battery_capacity);

		const totalUsedAhr = (device.inuse_ahr ?? 0) + (+device.add_inuse_ahr);

		let percentLifeRemainingAh = batteryMaxLifeAh ? ((batteryMaxLifeAh - totalUsedAhr) * 100) / batteryMaxLifeAh : 0;

		percentLifeRemainingAh = Math.max(0, percentLifeRemainingAh);
		return {
			percentLifeRemainingYears,
			percentLifeRemainingAh
		};
	}

	updateRTSync(macAddress, rtSync) {
		return this.httpClient.post(this.route + 'updateRTSync', {macAddress, rtSync}, {
			observe: "body"
		});
	}

	updateDeviceOnDB(mac_address: string, data: any) {
		return this.httpClient.post(this.route + 'updateDeviceOnDB', {mac_address, data}, {
			observe: "body"
		});
	}

	flushDeviceCache(macAddress) {
		return this.httpClient.post(this.route + 'flushDeviceCache', {macAddress}, {
			observe: "body"
		});
	}

	saveMaintenanceSchedule(macAddress, dirtyFields) {
		return this.httpClient.post(this.route + 'saveMaintenanceSchedule', {macAddress, dirtyFields}, {
			observe: "body"
		});
	}

	saveMultiMaintenanceSchedule(deviceIds, dirtyFields) {
		return this.httpClient.post(this.route + 'saveMultiMaintenanceSchedule', {deviceIds, dirtyFields}, {
			observe: "body"
		});
	}

	getTimeLostReport() {
		return this.httpClient.get(this.route + 'getTimeLostReport', {
			observe: "body"
		});
	}

	resetRTCLostDetection(macAddress) {
		return this.httpClient.post(this.route + 'resetRTCLostDetection', {macAddress}, {
			observe: "body"
		});
	}

	deviceLastConnectTime(macAddress, studyId: number) {
		let url = `${this.route}deviceLastConnectTime?macAddress=${encodeURIComponent(macAddress)}`;
		if (studyId)
			url += `&studyId=${studyId}`;
		return this.httpClient.get(url, {
			observe: "body",
			params: new CustomHttpParams({noUIBlock: true})
		});
	}

	officialReporting(
		macAddress,
		data: {
			included_in_reports?: boolean,
			is_feeder_meter?: number,
			is_meter_subtracting?: number,
			defective_device: any
		},
		wait = 0
	) {
		return this.httpClient.post(this.route + 'officialReporting', { macAddress, data, wait }, {
			observe: "body"
		});
	}

	getDeviceConfigAtShipping(macAddress: string) {
		return this.httpClient.get(`${this.route}device-config-at-shipping/${macAddress}`);
	}

	getDefectiveDevice(macAddress: string) {
		return this.httpClient.get(`${this.route}get-defective-device/${macAddress}`);
	}

	getChangesReportDevices() {
		return this.httpClient.get(this.route + 'getChangesReportDevices', {
			observe: "body"
		});
	}

	getDevicesConnectivityData(options) {
		return this.httpClient.get(`/reporting/get-devices-connectivity`, {
			params: options
		});
	}

	exportDevicesConnectivityCSV(options: any) {
		return this.httpClient.get(`/reporting/export-devices-connectivity-CSV`, {
			params: options
		});
	}

	commandCodeToName = {
		7:		'restart_device',
		d:		'indicator_light',
		9:		'set_serial_number',
		11:		'reset_data_history',
		'c':	'set_device_time',
		15:		'write_adc_override',
		16:		'read_adc_override',
		18:		'update_password',
		17:		'factory_reset',
		19:		'scan_wifi',

		2707:	'set_last_maintenance_date',
		2708:	'reverse_rt',
		2709:	'reverse_calibration',
		'270a':	'reverse_events',
		'270b':	'reset_rtc_lost_detection',
		'1a': 'query_cellular_info',
		'1b': 'cmd_cell_test_mode',
		'1c': 'cmd_cell_test_status'
	}

	getCommandNameFromCode(commandCode) {

		const hexCode = commandCode.toString(16);
		return this.commandCodeToName[hexCode]?  this.translateService.instant('command_name.'+ this.commandCodeToName[hexCode]) : '';
	}

	showManageDeviceOption(device: any, site?: any) {
		const hasManagementAccess = this.usersService.hasAccessFunction('manage_device');
		// special sites are never managed by non-SCT users
		const hasManagementSpecialSiteAccess = this.usersService.hasAccessFunction('special_site_device_management')
			&& !!this.usersService.getCurrentUser()?.isSCT;

		return this.usersService.hasAccessFunction('super_admin', 'write') ||
			(hasManagementAccess && (
				(this.deviceTypeService.isStudyDevice({application_type: device?.application_type, sct_device_type: device?.sct_device_type}) && site.name == this.commonUtil.powerStudiesDevicesSiteName) ||
				(!site.is_special) ||
				(hasManagementSpecialSiteAccess && site.is_special)
			)
		);
	}

	getSimInfo(iccid: string, isLive?: boolean, macAddress?: string) {
		return this.httpClient.post(`cellular/getSimInfo`, { iccid, isLive, mac_address: macAddress}, {
			observe: "body"
		});
	}

	getSimDevicesData(options: {from: number, to: number}) {
		return this.httpClient.post(`cellular/getSimDevicesData`, { options }, {
			observe: "body",
		});
	}

	getSimDailyAverageData(period: number) {
		return this.httpClient.get(`cellular/getSimDailyAverage`, {
			observe: "body",
			params: {
				period
			}
		});
	}

	getSimsDataByStatus(status: string) {
		return this.httpClient.get(`cellular/getSimsDataByStatus`, {
			observe: "body",
			params: {
				status
			}
		});
	}

	getSimUsageHistory(mac_address: string, from, to) {
		return this.httpClient.get(`cellular/getSimUsageHistory`, {
			params: { mac_address, from, to },
			observe: "body"
		});
	}

	getMissingSims() {
		return this.httpClient.get(`cellular/getMissingSims`, {
			observe: "body"
		});
	}

	refreshMissingSims() {
		return this.httpClient.post(`cellular/refreshMissingSims`, {}, {
			observe: "body"
		});
	}

	updateSimConfig(iccids: string[], operation: 'enable' | 'disable' | 'lock' | 'unlock', reason: string) {
		return this.httpClient.patch(`cellular/updateSimConfig`, {iccids, operation, reason}, {
			observe: "body"
		});
	}

	unblockSims(iccids: string[], reason: string) {
		return this.httpClient.post(`cellular/unblockSims`, {iccids, reason}, {
			observe: "body"
		});
	}

	resetCellularOEM(mac_address: string) {
		return this.httpClient.post(this.route + 'reset-oem', { mac_address}, {
			observe: "body"
		});
	}

	clearRTCTimeDriftWarning(mac_address: string) {
		return this.httpClient.post(this.route + 'clearRTCTimeDriftWarning', { mac_address}, {
			observe: "body"
		});
	}

	getCalibrationCertificateStatus(macAddress: string) {
		return this.httpClient.get<{ available: boolean }>('device/calibration-certificate-status', {
			observe: 'body',
			params: { macAddress }
		});
	}

	downloadCalibrationCertificate(macAddress: string) {
		return this.httpClient.get<{ name: string, content: { data: number[] } }>('device/calibration-certificate', {
			observe: 'body',
			params: { macAddress }
		});
	}

	validateRmaNo(value: string) {
		return /^RMA\d{10}$/.test(value);
	}

	getShippingConnectivityData(macAddress: string) {
		return this.httpClient.get(`device/getDeviceShippingConnectivity/${macAddress}`);
	}
	updateShippingConnectivityData(macAddress: string, action: "extend" | "cancel" | "renew") {
		return this.httpClient.patch(`device/updateDeviceShippingConnectivity`, { macAddress, action });
	}

	getAllQueueChanges() {
		return this.httpClient.get(`device/getAllQueueChanges`);
	}

	getNocMonitoringDevices() {
		return this.httpClient.get(this.route + 'getNocMonitoringDevices');
	}

	removeChangeStack(fieldName: string, commandId: number, macAddress: string) {
		return this.httpClient.post(`device/removeChangeStack`, {fieldName, commandId, macAddress});
	}

  extendQueuePeriod(commandIds: number[]) {
		return this.httpClient.post(`device/extendQueuePeriod`, { commandIds });
	}

	generateSuggestedInstallationReport(siteId: number, date: number, gapPeriod: number) {
		return this.httpClient.get('device/generate-suggested-installation-report', {
			observe: "body",
			params: {
				siteId,
				installationDateByInstaller: date,
				gapPeriod,
			}
		});
	}

	setDevicesInstallationDate(data: any) {
		return this.httpClient.post(`device/set-installation-date`, { devicesWithInstallationDate: data });
	}

	getDevicesLocationMismatchReport() {
		return this.httpClient.get(`device/getDevicesLocationMismatchReport`);
	}

	getAllDefectiveDevices() {
		return this.httpClient.get(`device/get-defective-devices`);
	}

	getMonthlyQuarterlyChargerUsageReport(mac_address: string, year: number, sct_device_type: number, siteId:number=0, studyId: number, options?: { truckType?: string, getAllYears?: boolean }) {
		return this.httpClient.post(this.route + 'getMonthlyQuarterlyChargerUsageReport', {mac_address, year, sct_device_type, siteId, studyId, options}, {
			observe: "body"
		});
	}

	getDailyDeviceUsageReport(from: number, to: number, mac_address: string='', sct_device_type: number, siteId: number=0, studyId: number, options?: { truckType: string }) {
		return this.httpClient.post(this.route + 'getDailyDeviceUsageReport', {from, to, mac_address, sct_device_type, siteId, studyId, options}, {
			observe: "body"
		});
	}

	exportDisconnectedDevicesReport(options: any) {
		return this.httpClient.get(this.route + 'exportDisconnectedDevicesReport', {
			params: options
		});
	}

	getQuickViewHistory(macAddress: string, startDate: number, endDate: number) {
		return this.httpClient.get(`device/getQuickViewHistory`, {
			observe: 'body',
			params: {
				macAddress,
				startDate,
				endDate
			}
		});
	}

	getDeviceUtilizationScoreData(mac_address: string, sct_device_types: number[], options: {studyId?: number} = {}) {
		return this.httpClient.post(this.route + 'getDeviceUtilizationScoreData', {mac_address, sct_device_types, options}, {
			observe: "body"
		});
	}

	getDeviceUtilizationScoreDataTruckReport(mac_address: string, sct_device_types: number[], options: {studyId?: number} = {}) {
		return this.httpClient.post(this.route + 'getDeviceUtilizationScoreDataTruckReport', {mac_address, sct_device_types, options}, {
			observe: "body"
		});
	}

	getPsReportData(mac_address: string, sct_device_types: number[], fromDate: number, toDate: number, options: {studyId?: number, siteId?: number} = {}) {
		return this.httpClient.post(this.route + 'getPsReportData', {mac_address, fromDate, toDate, sct_device_types, options}, {
			observe: "body"
		});
	}

	getEventsDefaultFields(deviceType?: number) {
		let eventsFieldOnOrder = this.eventsDefaultFields;

		const isImDevice = DeviceFactory.isIMDeviceType(deviceType);
		if (isImDevice)
			eventsFieldOnOrder = this.imEventsDefaultFields;

		return eventsFieldOnOrder;
	}

	getEventsFields(options: { all?: boolean, removeNotDisplayed?: boolean, deviceType?: number, fwVersion?: number } = {removeNotDisplayed: true}) {
		const objectMappers = this.commonUtil.getObjectMapper(options.deviceType);

		let eventsFieldOnOrder = [...this.getEventsDefaultFields(options.deviceType)];

		let eventsFields = Object.keys(objectMappers['events']).sort();
		eventsFields.push('event_duration', 'billed_kwh');
		eventsFields = eventsFields.map(field => this.eventCalculatedFieldsMapper[field] ? this.eventCalculatedFieldsMapper[field]: field);

		for(let field of eventsFields) {
			if (!eventsFieldOnOrder.includes(field))
				eventsFieldOnOrder.push(field);
		}

		if (options.all)
			return eventsFieldOnOrder;

		eventsFieldOnOrder = eventsFieldOnOrder.filter(field => !this.notDisplayedEventFields.includes(field));


		const filteredFields = [];

		for (const field of eventsFieldOnOrder) {
			if (this.notDisplayedEventFields.includes(field))
				continue;

			// Skip if FW-based field and FW doesn't support it
			if (this.eventsFieldsBasedOnFW.includes(field) && !this.commonUtil.isFWSupportField(field, options.fwVersion))
				continue;

			filteredFields.push(field);
		}

		eventsFieldOnOrder = filteredFields;

		return eventsFieldOnOrder;
	}

	translate(key: string, deviceOrType: { is_study_device: boolean, sct_device_type: number } | number): string {
		const translationKey = this.getTranslationKey(key, deviceOrType);
		return this.translateService.instant(translationKey);
	}

	getTranslationKey(key: string, deviceOrType: { is_study_device: boolean, sct_device_type: number } | number): string {
		const deviceType = typeof deviceOrType === 'number' ? deviceOrType : deviceOrType.sct_device_type;
		const deviceConfig: mainDeviceConfig = DeviceFactory.createDevice(deviceType).config;

		return `${deviceConfig.translationPath}${key}`;
	}

	// when you update this function, you should update the same function in backend
	isFWSupportedFeature(fwVersion: number, facture: string) {
		switch (facture) {
			case "disable_hall_effect":
				return fwVersion >= 2.43;
			case "disable_rt_kw":
				return fwVersion >= 2.4410;
			case "disable_rt_soc":
				return fwVersion >= 2.47;
			case 'battcom':
				return fwVersion >= this.commonUtil.CBL_MIN_FW_VERSION;
			default:
				return false;
		}
	}

	// when you update this function, you should update the same function in backend
	isHWVersionSupportedFeature(hwVersion: string, facture: string) {
		switch (facture) {
			case "disable_hall_effect":
				const pAsciiValue = this.commonUtil.asciiValue('AE');
				return this.commonUtil.asciiValue(hwVersion) >= pAsciiValue;
			default:
				return false;
		}
	}

	assignDeviceToBRMGroup(macAddress: string[], siteId: number, groupId: number, unassign: boolean = false) {
		return this.httpClient.post(this.route + 'assignDeviceToBRMGroup', { macAddress, siteId, groupId, unassign });
	}

	formatStaticIP(device: any, type: 'wifi' | 'ethernet') {
		let int2IpFields = [
			'static_ethernet_ip',
			'static_ethernet_gw',
			'static_ethernet_netmask',
			'static_cellular_ip',
			'static_cellular_gw',
			'static_cellular_netmask',
		];

		if (type === 'wifi')
			int2IpFields = [
				'static_wifi_ip',
				'static_wifi_gw',
				'static_wifi_netmask'
			];

		for (let field of int2IpFields) {
			if (device[field] && !this.validationService.ipAddressRegex.patt.test(device[field]))
				device[field] = this.commonUtil.int2ip(device[field]);
		}
	}

	getBilledKWHRsValueAndUnit(value: number = 0) {
		if (value < 900)
			return `${value.toFixed(2)} kWh`;

		if (value < 900000) {
			value /= 1000;
			return `${value.toFixed(2)} MWh`;
		}

		value /= 1000000;
		return `${value.toFixed(2)} GWh`;
	}

	/**
	 * Checks the IoTEmeter power thresholds and returns the message of every field that breaks a rule, keyed by
	 * field name so the caller can hand the result straight to sct-form's invalidFields.
	 * The whole number rule applies whichever events type the device is on; the greater than zero rule and the
	 * order rules only on State Based. A field missing from values is skipped, and an order rule needs both of
	 * its sides, so a multi devices save only checks what the user actually filled in - the backend is what
	 * checks each device against its own stored values.
	 * When you change this function, change the matching function in the backend (Common/validator.js).
	 */
	validateImPowerThresholds(values: ImPowerThresholdValues, options: {isStateBased: boolean}): {[field: string]: string} {
		const invalidFields: {[field: string]: string} = {};
		const label = (field: string) => this.translateService.instant(`devices.${field}_short`);
		const toNumber = (value: string | number | null | undefined) => {
			if (value === null || value === undefined || value === '')
				return null;

			const numberValue = Number(value);

			return Number.isFinite(numberValue) ? numberValue : null;
		};

		for (const field of this.imPowerThresholdFields) {
			if (!(field in values))
				continue;

			const value = toNumber(values[field]);

			if (value === null || !Number.isInteger(value)) {
				invalidFields[field] = this.translateService.instant('devices.power_threshold_whole_number', {
					field: label(field),
				});
				continue;
			}

			if (options.isStateBased && value < 1)
				invalidFields[field] = this.translateService.instant('devices.power_threshold_greater_than_zero', {
					field: label(field),
				});
		}

		if (!options.isStateBased)
			return invalidFields;

		for (const rule of this.imPowerThresholdOrderRules) {
			// A comparison needs two usable sides. When either side is already invalid on its own, that is the
			// specific thing to fix, and comparing against it would only drag its counterpart in with it.
			if (invalidFields[rule.field] || invalidFields[rule.atLeast])
				continue;

			const value = toNumber(values[rule.field]);
			const limit = toNumber(values[rule.atLeast]);

			// A side that was not submitted at all takes part in no comparison.
			if (value === null || limit === null || value >= limit)
				continue;

			const message = this.translateService.instant('devices.power_threshold_greater_than_or_equal', {
				field: label(rule.field),
				limitField: label(rule.atLeast),
			});

			invalidFields[rule.field] = message;
			invalidFields[rule.atLeast] = message;
		}

		return invalidFields;
	}

	/**
	 * Narrows the invalid thresholds down to the ones to put a message on, without changing what blocks the save.
	 * An order rule marks both of its sides, but the user only recognises the field they were working on, so when
	 * they changed one of the offending fields that is the one that carries the message. When they changed none of
	 * them the stored values were already invalid, so every offending field is marked rather than none - otherwise
	 * the save is blocked with nothing on screen to explain it.
	 */
	imThresholdMessagesToShow(
		invalidFields: {[field: string]: string},
		changedFields: {[field: string]: unknown},
	): {[field: string]: string} {
		const changed = Object.keys(invalidFields).filter(field => field in changedFields);

		if (!changed.length)
			return invalidFields;

		return changed.reduce((messages: {[field: string]: string}, field) => {
			messages[field] = invalidFields[field];

			return messages;
		}, {});
	}

	/** Devices on FW 2.51+ always fetch their debug records, so forcing a read does not apply to them (ST-2648). */
	isDebugFetchForced(configInfo: {min_forced_debug_id?: number | string | null}): boolean {
		return configInfo?.min_forced_debug_id !== null && configInfo?.min_forced_debug_id !== undefined;
	}
}
